// release-deploy-manual-receipt.ts
//
// Review P2 M4: what `resolve-deploy --outcome not-deployed` needs from the
// receipts when it takes back an earlier MANUAL `deployed` answer. Kept apart
// from release-deploy-completion-receipt.ts (the receipt format itself, at the
// 200-line ceiling) — this only reads and removes.
import { rm } from "node:fs/promises";
import { dirname } from "node:path";
import { type DurableFsOps, fsyncDirectory, realDurableFsOps } from "./durable-file-writer.js";
import {
  isWellFormedDeployReceipt,
  readReceipt,
  receiptPath,
} from "./release-deploy-completion-receipt.js";

/**
 * A readable smoke receipt exists for the candidate: the release already
 * counts as verified, so a person's answer can no longer undo the deploy
 * behind it. A missing or damaged one reads as "smoke not passed", like
 * readReleaseReceiptState.
 */
export async function smokeReceiptExists(
  controlRoot: string,
  candidateSha: string,
): Promise<boolean> {
  const read = await readReceipt(controlRoot, "smoke", candidateSha);
  return read.state === "ok" && read.body.status === "smoke_passed";
}

/**
 * What `removeManualDeployReceipt` did, and — when it left a receipt behind —
 * which kind: `real` = valid, written by a deploy (no `manual_resolution`),
 * `damaged` = unreadable or not shaped like a deploy receipt.
 */
export type ManualReceiptRemoval = "removed" | "absent" | "real" | "damaged";

/**
 * Removes the deploy receipt ONLY when it was built from a manual answer (it
 * carries `manual_resolution`). A receipt a real deploy wrote stays: it is the
 * journal's proof the recipe exited 0, not a person's say-so; a damaged one is
 * never guessed at. The caller must be able to tell the operator which.
 * The unlink is made durable (directory fsync) before returning: the caller
 * appends the journal next, and a receipt that reappears after a crash would
 * win over the retraction.
 */
export async function removeManualDeployReceipt(
  controlRoot: string,
  candidateSha: string,
  ops: DurableFsOps = realDurableFsOps,
): Promise<ManualReceiptRemoval> {
  const read = await readReceipt(controlRoot, "deploy", candidateSha);
  if (read.state === "absent") return "absent";
  if (read.state === "damaged" || !isWellFormedDeployReceipt(read.body)) return "damaged";
  const mark = read.body.manual_resolution;
  if (typeof mark !== "object" || mark === null) return "real";
  const path = receiptPath(controlRoot, "deploy", candidateSha);
  await rm(path, { force: true });
  await fsyncDirectory(dirname(path), ops);
  return "removed";
}
